import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, AbstractControl, Validators, FormArray } from '@angular/forms';
import { merge, Subscription } from 'rxjs';
import { valueOrDefault } from 'chart.js/dist/helpers/helpers.core';

export type ControlType = 'control' | 'group' | 'array';

export interface FlatControl {
  /** Percorso completo con dot notation per i gruppi e [] per gli array
   *  es: "address.city", "tags[0].label" */
  path: string;
  controlType: ControlType;
  value: unknown;
  valid: boolean;
  disabled: boolean;
  /** true se il control è un group/array con almeno un figlio disabled */
  hasDisabledChild: boolean;
  errors: Record<string, unknown> | null;
  dirty: boolean;
  touched: boolean;
  /** Profondità di annidamento (0 = primo livello) */
  depth: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getControlType(ctrl: AbstractControl): ControlType {
  if (ctrl instanceof FormArray) return 'array';
  if (ctrl instanceof FormGroup) return 'group';
  return 'control';
}

function anyChildDisabled(ctrl: AbstractControl): boolean {
  if (ctrl instanceof FormGroup) {
    return Object.values(ctrl.controls).some(c => c.disabled || anyChildDisabled(c));
  }
  if (ctrl instanceof FormArray) {
    return ctrl.controls.some(c => c.disabled || anyChildDisabled(c));
  }
  return false;
}

/**
 * Flatten ricorsivo di un AbstractControl nell'array `result`.
 * - FormGroup figli: dot notation  →  parent.child
 * - FormArray figli: bracket notation  →  parent[0]
 */
function flattenControls(
  ctrl: AbstractControl,
  path: string,
  depth: number,
  result: FlatControl[]
): void {
  const type = getControlType(ctrl);

  result.push({
    path,
    controlType: type,
    value: ctrl.value,
    valid: ctrl.valid,
    disabled: ctrl.disabled,
    hasDisabledChild: type !== 'control' ? anyChildDisabled(ctrl) : false,
    errors: ctrl.errors,
    dirty: ctrl.dirty,
    touched: ctrl.touched,
    depth,
  });

  if (type === 'group') {
    for (const [key, child] of Object.entries((ctrl as FormGroup).controls)) {
      flattenControls(child, `${path}.${key}`, depth + 1, result);
    }
  } else if (type === 'array') {
    (ctrl as FormArray).controls.forEach((child, idx) => {
      flattenControls(child, `${path}[${idx}]`, depth + 1, result);
    });
  }
}

@Component({
  selector: 'app-form-debug-inspector',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-debug-inspector.component.html',
  styleUrls: ['./form-debug-inspector.component.css'],
})
export class FormDebugInspectorComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;

  /** Se false il componente non renderizza nulla */
  @Input() visible: boolean = true;

  allControls: FlatControl[] = [];
  formattedJson = '';
  isFormValid = false;

  get validCount() {
    return this.allControls.filter(f => f.valid && !f.disabled).length;
  }
  get invalidCount() {
    return this.allControls.filter(f => !f.valid && !f.disabled).length;
  }
  get disabledCount() {
    return this.allControls.filter(f => f.disabled).length;
  }

  private sub?: Subscription;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (!this.formGroup) return;
    this.refresh();
    // statusChanges cattura enable()/disable() oltre ai cambiamenti di valore
    this.sub = merge(this.formGroup.valueChanges, this.formGroup.statusChanges).subscribe(() => {
      this.refresh();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private refresh(): void {
    const result: FlatControl[] = [];
    for (const [key, ctrl] of Object.entries(this.formGroup.controls)) {
      flattenControls(ctrl, key, 0, result);
    }
    this.allControls = result;
    this.isFormValid = this.formGroup.valid;
    this.formattedJson = this.syntaxHighlight(this.formGroup.value);
  }

  /**
   * Restituisce l'ultimo segmento del path per la visualizzazione nella cella.
   * "address.street"  → "street"
   * "tags[2].label"   → "[2].label"
   * "name"            → "name"
   */
  getLastSegment(path: string): string {
    const dotIdx = path.lastIndexOf('.');
    return dotIdx >= 0 ? path.slice(dotIdx + 1) : path;
  }

  getArrayLength(value: unknown): number {
    return Array.isArray(value) ? value.length : 0;
  }

  formatValue(val: unknown): string {
    if (val === null || val === undefined) return 'null';
    if (val === '') return '(empty)';
    if (typeof val === 'boolean') return String(val);
    if (typeof val === 'string') return `"${val}"`;
    if (typeof val === 'object') return '{…}';
    return String(val);
  }

  getErrorKeys(errors: Record<string, unknown> | null): string[] {
    return errors ? Object.keys(errors) : [];
  }

  private syntaxHighlight(value: unknown): string {
    const json = JSON.stringify(value, null, 2) ?? 'null';
    return json.replace(
      /("(?:\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?|[{}[\],:])/g,
      m => {
        if (/^"/.test(m))
          return /:$/.test(m)
            ? `<span style="color:#79c0ff">${m}</span>`
            : `<span style="color:#a5d6ff">${m}</span>`;
        if (/true|false/.test(m)) return `<span style="color:#ff7b72">${m}</span>`;
        if (/null/.test(m)) return `<span style="color:#8b949e">${m}</span>`;
        if (/[{}[\],:]/.test(m)) return `<span style="color:#8b949e">${m}</span>`;
        return `<span style="color:#f2cc60">${m}</span>`;
      }
    );
  }
}
